// @flow
import * as React from 'react';
import { SelectInput, TextInput } from '@coverright/ui/inputs';
import { useDebouncedMemo, useDeviceWidth } from '@coverright/utils';
import { ProviderOutput } from '@coverright/data-access/types/medicare';
import { V3InfoPanel } from '@coverright/ui/shared';
import { IconButton, InputAdornment, ListItemButton, ListItemText, MenuItem, Stack } from '@mui/material';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ZipSelectorCompact } from '@coverright/features';
import { useFindProvidersByFilterQuery } from '@coverright/data-access/medicare';
import trim from 'lodash/trim';

type Props = {
  zip: string;
  county: string;
  onSelect: (doctor: ProviderOutput) => void;
  showSearchRadius?: boolean;
  filterOptionsFunc: (
    doctor: ProviderOutput
  ) => boolean;
};

export function DoctorSelectionModalSearch(props: Props) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const deferredValue = useDebouncedMemo(searchTerm, 500);
  const [location, setLocation] = useState({zip: props.zip, county: props.county});
  const [radius, setRadius] = useState(30)
  const { data, loading } = useFindProvidersByFilterQuery({
    skip: !searchTerm,
    variables: {
      filterInput: {
        searchTerm: trim(deferredValue.replace('dr.', '').replace('Dr.', '')),
        zipCode: location.zip,
        radius
      }
    },
  })
  const doctors = useMemo(() => data?.findProvidersByFilter || [], [data])
  const ref = useRef<HTMLInputElement | null>(null);
  const phone = useDeviceWidth('sm')

  const filteredDoctors = (doctors as ProviderOutput[]).filter(props.filterOptionsFunc);

  useLayoutEffect(() => {
    ref.current?.focus()
  }, [])

  return (
    <div>
      {props.showSearchRadius && <Stack gap={2} direction={'row'} alignItems={'center'} mb={1}>
        <ZipSelectorCompact onChange={setLocation}
                            zip={location.zip}
                            county={location.county}
                            inputProps={{ label: 'Zip code' }} />
        <SelectInput label={'Radius'}
                     hideTick
                     MenuProps={{sx: {zIndex: 1502}}}
                     onChange={e => setRadius(e.target.value as number)} value={radius} className={'mb-0'}>
          <MenuItem value={30}>30</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
          <MenuItem value={200}>200</MenuItem>
          <MenuItem value={1000}>1000</MenuItem>
        </SelectInput>
      </Stack>}

      <TextInput value={searchTerm}
                 onChange={event => setSearchTerm(event.target.value)}
                 placeholder={'Search by name...'}
                 hideTick
                 inputRef={ref}
                 sx={{mb: 2}}
                 validate={false}
                 loading={loading}
                 InputProps={{
                   startAdornment: <InputAdornment position="start">
                     <MagnifyingGlass size={20} color={'#1C434F'} />
                   </InputAdornment>,
                   endAdornment: searchTerm && !loading && <InputAdornment position="end">
                     <IconButton size={'small'} onClick={() => { setSearchTerm(''); ref.current?.focus() }}>
                       <X size={20} color={'#1C434F'} />
                     </IconButton>
                   </InputAdornment>
                 }}
      />

      {filteredDoctors?.length > 0 && (
        <OverlayScrollbarsComponent
          style={{maxHeight: 400}}
          defer
        >
          {filteredDoctors.map((doctor) => (
            <ListItemButton onClick={() => props.onSelect(doctor)}>
              <ListItemText primaryTypographyProps={{sx: {fontWeight: 600} }}
                            primary={doctor.name} secondary={doctor.addresses[0]?.specialty} />
            </ListItemButton>
          ))}
        </OverlayScrollbarsComponent>
      )}


      {filteredDoctors.length === 0 && <V3InfoPanel
        sx={{ backgroundColor: '#F0F7FA' }}
        className={'find-doctor-helper'}
        showIcon={!phone}
        title={'Can’t find your doctor?'}
        text={
          'Don’t worry if you can’t find your doctor!  Our licensed insurance agents can conduct a more detailed search before finalizing your plan.  Enter what you can and we can do an initial search for those that are showing.  However, there may not be a plan in your area that covers all your doctors.'
        }
      />}
    </div>
  );
}

